<template>
  <pno-loading :loading="loading">
    <div class="container py-1">
      <br>
      <div class="row">
        <div class="col-md-4 col-lg-3">
          <div class="navbar-expand-md">
            <a class="btn btn-sm btn-outline-secondary d-block d-md-none mb-2" data-toggle="collapse" data-target="#searchFilters" aria-controls="searchFilters" aria-expanded="false" aria-label="Toggle search filters">
              Toggle search filters <i class="fal fa-lg fa-sliders-v ml-1"></i>
            </a>
            <div class="collapse navbar-collapse" id="searchFilters">
              <form class="search-filters pt-1" @submit.prevent="() => false">

                <!-- Category/features -->
                <div class="form-group small">
                  <legend class="col-form-label">Type</legend>
                  <div class="accordion" id="trailer-type">
                    <div v-for="category in searchCategories" :key="category">
                      <div class="headline" :id="'trailer-headline-' + category" data-toggle="collapse" :data-target="'#trailer-type-' + category" aria-expanded="true" v-on:click="filter.category = category">
                        <div class="form-check" >
                          <input class="form-check-input mt-1" type="radio" name="trailer-type" :id="'type-' + category" :value="category" v-model="filter.category">
                          <label class="form-check-label" :for="'type-' + category">{{ trailerCategoryName(category) }} <small class="grey d-none">({{ categoryCount(category) }})</small></label>
                        </div>
                      </div>
                      <div :id="'trailer-type-' + category" :class="'content collapse'" :aria-labelledby="'trailer-headline-' + category" data-parent="#trailer-type">
                        <div class="form-check" v-for="(count, feature) in searchFeatures(category)" :key="category + feature">
                          <div>
                            <input class="form-check-input" type="checkbox" :value="feature" :id="category + feature" v-model="filter.features" :disabled="count == 0 && !filter.features.includes(feature)">
                            <label class="form-check-label" :for="category + feature">{{ trailerFeatureName(feature) }} <small class="grey">({{ safeCount(count, 50)}})</small></label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Height -->
                <div class="form-group small">
                  <legend class="col-form-label">Maximum height</legend>
                  <select v-model="filter.max_height" class="custom-select form-control-sm">
                    <option value="">Any</option>
                    <option value="4000">400 cm</option>
                    <option value="4100">410 cm</option>
                    <option value="4200">420 cm</option>
                    <option value="4300">430 cm</option>
                    <option value="4400">440 cm</option>
                    <option value="4500">450 cm</option>
                  </select>
                </div>

                <hr>

                <!-- Locations -->
                <div class="form-group small">
                  <legend class="col-form-label">Location</legend>
                  <div class="accordion" id="country-location">
                    <div v-for="country in countries" :key="'country-' + country">
                      <div class="headline" :id="'country-headline-' + country" data-toggle="collapse" :data-target="'#country-location-' + country" aria-expanded="true" v-on:click="filter.country = country">
                        <div class="form-check">
                          <input class="form-check-input mt-1" type="radio" name="trailer-country" :value="country" :id="'country-' + country" v-model="filter.country">
                          <label class="form-check-label" :for="'country-' + country">{{ countryName(country) }} <small class="grey d-none">({{ countryCount(country) }})</small></label>
                        </div>
                      </div>
                      <div :id="'country-location-' + country" :class="'content collapse' + (filter.country == country ? ' show' : '')" :aria-labelledby="'country-headline-' + country" data-parent="#country-location">
                        <div class="form-check" v-for="city in cities(country)" :key="'location-' + city">
                          <div>
                            <input class="form-check-input mt-1" type="checkbox" :value="city" :id="'location-' + city" v-model="filter.city">
                            <label class="form-check-label" :for="'location-' + city">{{ city }} <small class="grey d-none">({{ cityCount(city) }})</small></label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <hr>

                <!-- Manufacturers -->
                <div class="form-group small">
                  <legend class="col-form-label">Manufacturer</legend>
                  <template v-for="(count, manufacturer) in counts.manufacturers">
                    <div class="form-check" :key="manufacturer" v-if="(count > 0 || filter.manufacturer.includes(manufacturer)) && manufacturer">
                      <input class="form-check-input mt-1" type="checkbox" :value="manufacturer" :id="manufacturer" v-model="filter.manufacturer">
                      <label class="form-check-label text-capitalize" :for="manufacturer">{{ manufacturer }} <small class="grey d-none">({{ count }})</small></label>
                    </div>
                  </template>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div class="col-md-8 col-lg-9 pt-1">
          <h1 class="h5"><span class="h4">{{ safeCount(totalAssets, 50) }}</span> trailers</h1>
          <p class="grey mb-2">
            Filters:

            <button disabled type="button" class="btn btn-sm btn-xs btn-outline-secondary ml-1 mt-1 mb-2">{{ countryName(filter.country) }}</button>

            <button
              v-for="city in filter.city"
              :key="city"
              type="button"
              class="btn btn-sm btn-xs btn-outline-secondary ml-1 mt-1 mb-2"
              @click="removeFilterOption(filter.city, city)"
            >{{ city }} <i class="far fa-times-circle"></i></button>

            <button disabled type="button" class="btn btn-sm btn-xs btn-outline-secondary ml-1 mt-1 mb-2">{{ trailerCategoryName(filter.category) }}</button>

            <button
              v-for="feature in filter.features"
              :key="feature"
              type="button"
              class="btn btn-sm btn-xs btn-outline-secondary ml-1 mt-1 mb-2"
              @click="removeFilterOption(filter.features, feature)"
            >{{ trailerFeatureName(feature) }} <i class="far fa-times-circle"></i></button>

            <button
              v-if="filter.max_height"
              type="button"
              class="btn btn-sm btn-xs btn-outline-secondary ml-1 mt-1 mb-2"
              @click="filter.max_height = ''"
            >Max {{ filter.max_height / 10 }} cm <i class="far fa-times-circle"></i></button>

            <button
              v-for="manufacturer in filter.manufacturer"
              :key="manufacturer"
              type="button"
              class="btn btn-sm btn-xs btn-outline-secondary ml-1 mt-1 mb-2 text-capitalize"
              @click="removeFilterOption(filter.manufacturer, manufacturer)"
            >{{ manufacturer }} <i class="far fa-times-circle"></i></button>

          </p>
          <div v-if="totalAssets == 0" class="alert alert-info py-4">
            <p class="font-weight-bold">No matching results 😥</p>
            <p>Change filters to show alternatives. Or reach out today!</p>
            <p>PNO has the Nordic’s largest standard and specialty trailer fleet for short-term rental, and we can specify, order and deliver fully-customisable trailers that match your needs.</p>
            <a href="https://pnorental.com/contact/" class="btn btn-info">Get in touch</a>
          </div>
          <div class="row">
            <div class="col-sm-6 col-lg-4 mb-4" v-for="asset in assets" :key="asset.assetId">
              <pno-trailer :trailer="asset" />
            </div>
          </div>
          <p class="mt-2"><button type="button" class="btn btn-sm btn-block btn-outline-secondary" v-on:click="loadMore" v-if="showingAssets < totalAssets">More results <i class="far fa-chevron-circle-down"></i></button></p>
        </div>
      </div>
    </div>
  </pno-loading>
</template>

<script>
import PnoTrailer from '@/views/trailers/PnoTrailer.vue'
import { mapState, mapMutations } from 'vuex'
import Vue from 'vue'

export default {
  data: () => ({
    isActive: false,
    isAlive: false,
    loading: true,
    assets: [], // assets found
    counts: {
      categories: [], // categories + counts found
      features: [], // features + counts found
      manufacturers: [], // manufacturers + counts found
      locations: {}, // locations + counts found
    },
    skip: 0,
    limit: 9,
    totalAssets: 0,
    filter: {},
    oldFilter: {},
    formDateFormat: 'DD/MM-YYYY',
    defaultFilter: {
      category: 'curtain',
      features: [],
      manufacturer: [],
      max_height: '',
      country: 'SWE',
      city: [],
    },
    searchCategories: ['curtain', 'reefer', 'box', 'movingfloor', 'dolly', 'flatbed', 'container'],
    searchFeaturesFound: {},
  }),
  methods: {
    safeCount(count, max) {
      return count > max ? max + '+' : count
    },
    depotsByCountry(countryCode) {
      if (this.countryDepots[countryCode]) {      
        return this.countryDepots[countryCode]
      }
    },
    cities(country) {
      return Array.from(this.searchCities.get(country))
    },
    countryName(country) {
      return this.countryNames[country] ? this.countryNames[country] : this.countryNames[this.defaultFilter.country]
    },
    countryCount(country) {
      //if (!this.count.locations) return 0
      let count = 0
      for (let [depot, depotCount] of Object.entries(this.counts.locations)) {
        if (this.allLocations[depot] && this.allLocations[depot].countryCode == country) count += depotCount
      }
      return count
    },
    cityCount(city) {
      //if (!this.count.locations) return 0
      let count = 0
      for (let [depot, depotCount] of Object.entries(this.counts.locations)) {
        if (this.allLocations[depot] && this.allLocations[depot].city == city) count += depotCount
      }
      return count
    },
    categoryCount(category) {
      return this.counts.categories[category] ? this.counts.categories[category] : 0
    },
    removeFilterOption(arr, val) {
      arr.splice(arr.indexOf(val), 1)
    },
    ...mapMutations('assets', ['resetFilter', 'persistFilter']),
    getFilter(include_total) {
      const filter = {}
      filter.skip = this.skip
      filter.limit = this.limit
      filter.category = this.filter.category
      filter.features = this.filter.features
      filter.location = this.searchDepots(this.filter.country)
      filter.manufacturer = this.filter.manufacturer
      filter.max_height = this.filter.max_height
      filter.include_total = include_total
      return filter
    },
    loadMore: async function () {
      this.loading = true
      this.skip += this.limit
      const filter = this.getFilter(false)
      const nextPage = await this.$store.dispatch('assets/findAvailableAssets', filter)
      if (nextPage) {
        const self = this
        nextPage.assets.forEach(function (asset) {
          self.assets.push(asset)
        })
      }
      this.loading = false
    },
    async refresh() {
      this.loading = true
      this.skip = 0
      const filter = this.getFilter(true)
      const featureParams = filter
      featureParams.countryDepots = this.depotsByCountry(this.filter.country)
    
      const promises = []
      promises.push(this.$store.dispatch('assets/findAvailableAssets', filter).then((result) => { this.assets = result.assets; this.totalAssets = result.total }))
      promises.push(this.$store.dispatch('assets/getCategories', filter).then(categories => this.counts.categories = categories))
      promises.push(this.$store.dispatch('assets/getFeatures', featureParams).then(features => this.$set(this.searchFeaturesFound, this.filter.category, features)))
      promises.push(this.$store.dispatch('assets/getManufacturers', filter).then(manufacturers => this.counts.manufacturers = manufacturers))
      promises.push(this.$store.dispatch('assets/getLocations', filter).then(locations => this.counts.locations = locations))
      for (let category of this.searchCategories) {
        if (category == this.filter.category) continue
        const subFilter = Object.assign({}, filter, { category })
        delete subFilter.features
        promises.push(this.$store.dispatch('assets/getFeatures', subFilter).then(features => this.$set(this.searchFeaturesFound, category, features)))
      }
      await Promise.all(promises)
      this.loading = false
    },
    // Derive depot names from location search filter
    searchDepots() {
      var searchDepots = []

      const country = this.searchCities.has(this.filter.country) ? this.filter.country : this.defaultFilter.country
      const searchCity = this.filter.city.length == 0 ? this.searchCities.get(country) : this.filter.city
      for (let city of searchCity) {
        searchDepots = searchDepots.concat(Object.keys(this.allLocations).map(depot => this.allLocations[depot].city == city && this.allLocations[depot].countryCode == country ? depot : null)).filter(Boolean)
      }
      return searchDepots
    },
    searchFeatures(category) {
      return this.searchFeaturesFound[category] ? this.searchFeaturesFound[category] : {}
    },
    activate() {
      this.isAlive = false

      if (!this.$router.currentRoute.query.category) {
        // No query arguments. Load filter from localstorage, and set proper url
        this.filter = Object.assign({}, this.defaultFilter, this.$localStorage.get('filter', {}))
      } else {
        // Got query arguments. Update localstorage
        this.filter = Object.assign({}, this.defaultFilter, this.$router.currentRoute.query)
      }
      if (!this.searchCities.has(this.filter.country)) {
        this.filter.country = this.defaultFilter.country
      }
      this.oldFilter = Object.assign({}, this.filter)

      // Don't refresh if already active (keep-alive)
      if (this.isActive) return

      this.isActive = true
      this.$nextTick(() => $('#trailer-type-' + this.filter.category).collapse('show'))
      this.$nextTick(() => this.refresh())
    },
  },
  mounted() {
    this.$store.commit('meta/title', 'Search trailers')
    this.$store.commit('meta/description', 'Find trailers for short-term rental or long-term leasing.')
    this.activate()
  },
  components: { PnoTrailer },
  computed: {
    showingAssets() {
      return Math.min(this.totalAssets, this.skip + this.limit)
    },
     ...mapState({countryDepots: state => state.locations.countryDepots }),
     ...mapState({countryNames: state => state.locations.countryNames }),
     ...mapState({searchCities: state => state.locations.searchCities[0] }),
     ...mapState({allLocations: state => state.locations.locations}),
    countries() {
      return Array.from(this.searchCities.keys())
    },
  },
  watch: {
    filter: {
      handler: async function (filter) {
        let refresh = true

        if (this.oldFilter.category !== filter.category) {
          this.features = {}
          this.filter.features = []
          this.counts.features = {}
          this.oldFilter = Object.assign({}, filter)
          refresh = false
        }
        if (this.oldFilter.country !== filter.country) {
          this.features = {}
          this.filter.features = []
          this.counts.features = {}
          this.filter.city = []
          refresh = false
        }

        this.oldFilter = Object.assign({}, filter)
        if (refresh) {
          this.$localStorage.set('filter', filter)

          // Don't refresh here on initial load, as this is done by the activated event.
          if (this.isAlive) {
            this.$nextTick(() => this.refresh())
          }
          this.isAlive = true

          // When search criterias are changed, make sure browser url reflects search results, but don't reload component.
          const fullPath = this.$router.currentRoute.path + this.$router.options.stringifyQuery(this.filter)
          window.history.replaceState({}, 'Search trailers', fullPath)
        }
      },
      deep: true,
    },
  }
}
</script>
